import { useState, useEffect } from "react";
import ArrowLeft from "./Icons/MenuArrowLeft";
import ArrowRight from "./Icons/MenuArrowRight";
import "./Menu.css";
import ToastNotification from "./Toast/ToastNotification";
import useToast from "./Toast/useToast";
import { motion } from "framer-motion";
import SellIcon from "./Icons/SellIcon.js";
import { useTranslation } from "react-i18next";
import { Howler } from "howler";
import AcceptIcon from "./Icons/AcceptIcon.js";
import DeclineIcon from "./Icons/DeclineIcon.js";
import { formatNumber, getAuraByName, getRarityColor } from "./Functions.js";
import ConfirmMessage from "./ConfirmMessage.js";
import StatsIcon from "./Icons/StatsIcon.js";
import FriendsIcon from "./Icons/FriendsIcon.js";
import ChangeNameIcon from "./Icons/ChangeNameIcon.js";
import StatusIcon from "./Icons/StatusIcon.js";
import SendDucksIcon from "./Icons/SendDucksIcon.js";
import SoundOnIcon from "./Icons/SoundOnIcon.js";
import SoundOffIcon from "./Icons/SoundOffIcon.js";
import audioManager from "./AudioManager.js";
import { useSocket } from "./SocketContext.js";

const Menu = ({
  onChangeName,
  onChangeDescription,
  onSendDucks,
  localProfile,
  setStorageItem,
  readProfile,
}) => {
  const { toasts, addToast } = useToast();
  const { emit, on, off, once, isConnected } = useSocket();

  const [menuPage, setMenuPage] = useState("main");

  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [recieverId, setRecieverId] = useState("");
  const [friendId, setFriendId] = useState(null);
  const [ducksCount, setDucksCount] = useState();

  const { t, i18n } = useTranslation();
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [friends, setFriends] = useState([]);

  const [isConfirmRemoveFriend, setIsConfirmRemoveFriend] = useState(false);
  const [auraColors, setAuraColors] = useState({});
  useEffect(() => {
    const fetchAuras = async () => {
      const colors = {};
      for (const friend of friends) {
        if (friend.mainAura) {
          const aura = await getAuraByName(friend.mainAura);
          colors[friend.id] = aura?.color || "gray"; // Дефолтный цвет
        }
      }
      setAuraColors(colors);
    };

    if (friends?.length) {
      fetchAuras();
    }
  }, [friends]);

  useEffect(() => {
    if (!localProfile) return;
    // Слушаем событие обновления профиля
    on("friend_request_accepted", async () => {
      const profile = await readProfile(localProfile.tgId);
      console.log("accepted", profile);
      setReceivedRequests(profile.friendRequestsReceived);
      setFriends(profile.friends);
    });

    // Слушаем событие обновления заявок
    on("friend_request_rejected", async () => {
      const profile = await readProfile(localProfile.tgId);
      console.log("rejected", profile);
      setReceivedRequests(profile.friendRequestsReceived);
    });

    on("friend_removed", async () => {
      const profile = await readProfile(localProfile.tgId);
      setFriends(profile.friends);
      setFriendToRemove(null);
    });

    on("friend_request_sent", async () => {
      addToast(t("Request sent"), "info");
    });
    on("friend_request_sent_error", async ({ message }) => {
      addToast(message, "error");
    });

    // Очистка при размонтировании
    return () => {
      off("friend_request_accepted");
      off("friend_request_rejected");
      off("friend_removed");
      off("friend_request_sent");
      off("friend_request_sent_error");
    };
  }, [localProfile]);

  const fetchPlayerData = async () => {
    if (!localProfile?.tgId) return;
    try {
      const profile = await readProfile(localProfile.tgId);
      return {
        friends: profile.friends,
        receivedRequests: profile.friendRequestsReceived,
      };
    } catch (error) {
      console.error("Ошибка при получении данных:", error);
      return { friends: [], receivedRequests: [] };
    }
  };

  useEffect(() => {
    if (menuPage === "friendRequest") {
      fetchPlayerData().then(async ({ friends, receivedRequests }) => {
        setReceivedRequests(receivedRequests);
        setFriends(friends);
      });
    }
  }, [menuPage]); // Добавлен localProfile

  const handleSendFriendRequest = (e) => {
    e.preventDefault();

    const id = parseInt(friendId);
    if (isNaN(id)) {
      addToast(t("Incorrect ID"), "error");
      return;
    }

    if (!localProfile?.tgId || !localProfile?.id) return;

    if (id === localProfile.id) {
      addToast(t("You can't send a request to yourself"), "error");
      return;
    }

    const isRequestSent = localProfile.friendRequestsSent.some(
      (request) => request.id === id
    );

    const isFriends = localProfile.friends.some((friend) => friend.id === id);

    if (isRequestSent) {
      addToast(t("Already sent"), "error");
      return;
    }

    if (isFriends) {
      addToast(t("Already friends"), "error");
      return;
    }

    // Всё ок — отправляем
    emit("send_friend_request", {
      senderId: localProfile.id,
      receiverId: id,
    });
  };

  const handleAcceptRequest = async (requestId) => {
    if (!localProfile?.id) return;
    try {
      // Отправляем событие через WebSocket
      emit("accept_friend_request", {
        playerId: localProfile.id,
        friendId: requestId,
      });

      // Уведомление об успешнfом удалении
      addToast(t("Friend added"), "success");
    } catch (error) {
      addToast(t("Error while adding a friend"), "error");
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!localProfile?.id) return;
    try {
      // Отправляем событие через WebSocket
      emit("reject_friend_request", {
        playerId: localProfile.id,
        friendId: requestId,
      });

      // Обновляем список заявок
      const { receivedRequests } = await fetchPlayerData();
      setReceivedRequests(receivedRequests);
      addToast(t("Request denied"), "success");
    } catch (error) {
      addToast(error, "error");
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!localProfile?.id) return;
    try {
      // Отправляем событие через WebSocket
      emit("remove_friend", {
        playerId: localProfile.id,
        friendId: friendId,
      });
      // Уведомление об успешном удалении
      setIsConfirmRemoveFriend(false);
      addToast(t("Friend removed"), "success");
    } catch (error) {
      addToast(t("Error while deleting friend"), "error");
    }
  };

  useEffect(() => {
    audioManager.play("s_gameSwitch");
  }, [menuPage]);

  const changeLanguage = async (language) => {
    i18n.changeLanguage(language);
    await setStorageItem("language", language);
  };
  const toggleLanguage = () => {
    audioManager.play("s_gameSwitch");
    i18n.language === "ru" ? changeLanguage("en") : changeLanguage("ru");
  };
  const [isMuted, setIsMuted] = useState(false);

  const toggleSound = () => {
    audioManager.play("s_gameSwitch");
    setIsMuted(!isMuted); // Меняем состояние
    Howler.mute(!isMuted); // Включаем/выключаем звуки
  };

  const handleDescriptionChange = (e) => {
    const { value } = e.target;
    if (value.length <= 30) {
      setNewDescription(e.target.value);
    }
  };

  const handleSubmitName = (e) => {
    e.preventDefault();
    if (!localProfile?.ducks) return;
    if (localProfile.ducks < 100000) {
      addToast(t("not enough ducks"), "error");
      return;
    }
    onChangeName(newName);
    setMenuPage("main");
  };

  const handleSubmitDescription = (e) => {
    e.preventDefault();
    if (!localProfile?.ducks) return;
    if (localProfile.ducks < 9900) {
      addToast(t("not enough ducks"), "error");
      return;
    }
    onChangeDescription(newDescription);
    setMenuPage("main");
  };

  const handleSubmitDucks = (e) => {
    e.preventDefault();
    if (!localProfile?.id) return;
    if (parseInt(recieverId) !== localProfile.id) {
      onSendDucks(parseInt(recieverId), parseInt(ducksCount));
      setMenuPage("main");
    } else {
      addToast(t("same id"), "error");
    }
  };

  const [friendToRemove, setFriendToRemove] = useState(null);
  const openConfirmRemoveFriend = (friend) => {
    setFriendToRemove(friend);
    setIsConfirmRemoveFriend(true);
  };

  return (
    <div className="menu-container">
      <div className="menu-header">
        <ArrowLeft
          style={{
            opacity: menuPage === "main" ? 0 : 1,
            pointerEvents: menuPage === "main" ? "none" : "auto",
          }}
          onClick={() => setMenuPage("main")}
          size="16px"
        ></ArrowLeft>
        <div>ID: {localProfile?.id}</div>
      </div>
      {menuPage === "main" && (
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          style={{ display: menuPage === "main" ? "grid" : "none" }}
          className="menu-items"
        >
          <div
            onClick={() => setMenuPage("friendRequest")}
            className="menu-item"
          >
            <FriendsIcon size="48px" />
            <div>{t("Friends")}</div>
          </div>
          <div onClick={() => setMenuPage("sendDucks")} className="menu-item">
            <SendDucksIcon size="48px" />
            <div>{t("send ducks")}</div>
          </div>
          <div onClick={() => setMenuPage("changeName")} className="menu-item">
            <ChangeNameIcon size="42px" />
            <div>{t("name change")}</div>
          </div>
          <div
            onClick={() => setMenuPage("changeDescription")}
            className="menu-item"
          >
            <StatusIcon size="48px" />
            <div>{t("setDescription")}</div>
          </div>

          <div onClick={() => setMenuPage("statistics")} className="menu-item">
            <StatsIcon size="48px" />
            <div>{t("Statistics")}</div>
          </div>
          <div onClick={toggleSound} className="menu-item">
            {!isMuted && <SoundOffIcon size="48px" />}
            {isMuted && <SoundOnIcon size="48px" />}
            <div>{isMuted ? t("sound on") : t("sound off")}</div>
          </div>
          <div onClick={() => toggleLanguage()} className="menu-item">
            <SellIcon size="48px"></SellIcon>
            <div>{t("changeLang")}</div>
          </div>
        </motion.div>
      )}
      {menuPage === "statistics" && (
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          className="statistics-container"
        >
          <div className="statistics-item-container">
            <div className="statistics-item">{t("Best combo: ")}</div>
            <div className="statistics-item">{localProfile?.bestCombo}</div>
          </div>
          <div className="statistics-item-container">
            <div className="statistics-item">{t("Spins: ")}</div>
            <div className="statistics-item">{localProfile?.spins}</div>
          </div>
        </motion.div>
      )}
      {menuPage === "changeName" && (
        <motion.form
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          onSubmit={handleSubmitName}
          className="change-name-container"
        >
          <input
            onChange={(e) => setNewName(e.target.value)}
            required={true}
            type="text"
            id="newName"
            name="newName"
            maxLength={16}
            minLength={2}
            className="name-input"
            placeholder={localProfile?.firstName}
          ></input>
          <div className="change-name-description">
            {t("cost") + ": 100000 DUCK$"}
          </div>
          <button style={{ padding: "8px 12px" }} className="menu-button">
            {t("name change")}
          </button>
        </motion.form>
      )}
      {menuPage === "changeDescription" && (
        <motion.form
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          onSubmit={handleSubmitDescription}
          className="change-name-container"
        >
          <input
            onChange={handleDescriptionChange}
            value={newDescription}
            required={true}
            type="text"
            id="newDescription"
            name="newDescription"
            minLength={1}
            className="name-input"
            placeholder={localProfile?.description}
          ></input>
          <div className="change-name-description">
            {t("cost") + ": 9900 DUCK$"}
          </div>
          <button style={{ padding: "8px 12px" }} className="menu-button">
            {t("setDescription")}
          </button>
        </motion.form>
      )}
      {menuPage === "sendDucks" && (
        <motion.form
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          onSubmit={handleSubmitDucks}
          className="change-name-container"
        >
          <input
            onChange={(e) => setRecieverId(e.target.value)}
            required={true}
            type="number"
            id="recieverId"
            name="recieverId"
            maxLength={20}
            step="1"
            minLength={5}
            className="name-input"
            placeholder={t("RecipientID")}
          ></input>
          <input
            onChange={(e) => setDucksCount(e.target.value)}
            required={true}
            step="1"
            type="number"
            id="ducks"
            name="ducks"
            maxLength={20}
            min={100}
            max={localProfile?.ducks}
            className="name-input"
            placeholder={t("amount")}
          ></input>
          <div className="change-name-description">{t("know id")}</div>
          <button style={{ padding: "8px 12px" }} className="menu-button">
            {t("send")}
          </button>
        </motion.form>
      )}
      {menuPage === "friendRequest" && (
        <>
          {isConfirmRemoveFriend && (
            <ConfirmMessage
              message={t("Are you sure you want to delete a friend?")}
              onConfirm={() => handleRemoveFriend(friendToRemove.id)}
              onCancel={() => setIsConfirmRemoveFriend(false)}
            />
          )}
          <motion.form
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            onSubmit={handleSendFriendRequest}
            className="change-name-container"
          >
            {/* Поле для ввода ID друга */}
            <input
              onChange={(e) => setFriendId(e.target.value)}
              required={true}
              type="number"
              id="recieverId"
              name="recieverId"
              maxLength={20}
              step="1"
              minLength={1}
              className="name-input"
              placeholder={t("RecipientID")}
            ></input>

            <button
              style={{
                padding: "8px 12px",
                width: "30%",
                justifyContent: "center",
              }}
              className="menu-button"
            >
              {t("Add")}
            </button>
          </motion.form>

          {/* Полученные заявки */}
          {receivedRequests?.length > 0 && (
            <div className="requests-section">
              <h3 style={{ marginBottom: 10 }}>{t("Friend requests")}</h3>
              <div className="friends-list">
                {receivedRequests.map((request) => (
                  <div key={request.id} className="request-item">
                    <span>{request.firstName}</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        className="menu-button"
                        style={{
                          padding: "4px",
                          backgroundColor: "rgb(12, 117, 4)",
                        }}
                        onClick={() => handleAcceptRequest(request.id)}
                      >
                        <AcceptIcon size={32}></AcceptIcon>
                      </button>
                      <button
                        className="menu-button"
                        style={{
                          padding: "4px",
                        }}
                        onClick={() => handleRejectRequest(request.id)}
                      >
                        <DeclineIcon size={32}></DeclineIcon>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Друзья */}
          <div className="requests-section">
            <h3 style={{ marginBottom: 10 }}>{t("Friends")}</h3>
            <div className="friends-list">
              {friends?.length > 0 ? (
                friends.map((friend) => (
                  <div key={friend.id} className="request-item">
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <span style={{ opacity: 0.5 }}>ID: {friend.id}</span>
                      <span>{friend.firstName}</span>

                      <span
                        style={{
                          backgroundColor:
                            auraColors[friend.id] ||
                            "rgba(255, 255, 255, 0.34)",
                          color: "black",
                          padding: "5px 8px",
                          borderRadius: 8,
                        }}
                      >
                        {" 1/" + formatNumber(friend.mainAuraRarity)}
                      </span>
                      {friend.isOnline && (
                        <span className="status-text online">
                          {t("Online")}
                        </span>
                      )}
                      {!friend.isOnline && (
                        <span className="status-text offline">
                          {t("Offline")}
                        </span>
                      )}
                    </div>

                    <button
                      className="menu-button"
                      style={{
                        padding: "4px",
                      }}
                      onClick={() => openConfirmRemoveFriend(friend)}
                    >
                      <DeclineIcon size={24}></DeclineIcon>
                    </button>
                  </div>
                ))
              ) : (
                <p>{t("You have no friends.")}</p>
              )}
            </div>
          </div>
        </>
      )}

      <ToastNotification toasts={toasts} />
    </div>
  );
};
export default Menu;
